import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Paper, Chip, Button, Table, TableHead, TableBody,
  TableRow, TableCell, Switch, FormControlLabel, TextField, CircularProgress,
  Alert, Dialog, DialogTitle, DialogContent, DialogActions, Select,
  MenuItem, IconButton,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { db } from '../../../firebase/config';
import {
  collection, query, orderBy, limit, onSnapshot, getDocs, addDoc,
  deleteDoc, doc, updateDoc, serverTimestamp, where, getDoc,
} from 'firebase/firestore';
import { APPLAB_ACCENT } from '../constants';

const cellSx = { color: '#ccc', borderColor: 'rgba(255,255,255,0.06)', fontSize: '0.8rem', py: 1 };
const headSx = { color: '#888', borderColor: 'rgba(255,255,255,0.08)', fontSize: '0.7rem', fontWeight: 700, py: 1 };

// ── Section nav chip strip ─────────────────────────────────────────────────────
const SECTIONS = ['Översikt', 'Användarkvoter', 'Referensbibliotek', 'Brief-exempel', 'Inställningar', 'Kostnadsspårning'];

// ── 18 seed examples ──────────────────────────────────────────────────────────
const SEED_EXAMPLES = [
  { title: 'Digitalt projektrum – fastighetsförvaltning', situation: 'early', customer_type: 'Fastighetsägare', brief: 'Visa hur ett modernt digitalt projektrum kan se ut för deras fastighetsförvaltning. Inkludera en dashboard med aktiva underhållsprojekt, dokumentöversikt och incidentrapportering. Betona tydlig strukturering och enkel navigering för icke-tekniska användare.' },
  { title: 'BIM-koordineringscenter', situation: 'early', customer_type: 'Byggentreprenör', brief: 'Demonstrera hur ett BIM-koordineringscenter kan samla modeller från Revit och Navisworks. Visa modellistatus per disciplin (arkitektur, el, VVS, konstruktion), kollisionskontroll-sammanfattning och ansvariga BIM-samordnare.' },
  { title: 'Digital tvilling – kontorsbyggnad', situation: 'early', customer_type: 'Fastighetsägare', brief: 'Skapa en vision för en digital tvilling av deras kontorsfastighet. Visa realtidsdata från sensorer (temperatur, fukt, energiförbrukning), planvy per våning och driftstatus för tekniska system. Stilpreset: tekniskt.' },
  { title: 'Projektstyrningsportal för infrastruktur', situation: 'early', customer_type: 'Beställare/Byggherre', brief: 'Visa hur ett modernt verktyg för att styra komplexa infrastrukturprojekt kan se ut. Inkludera milstolpar och Gantt-vy, budgetstatus, riskmatris och dokumenthantering. Kunderna är projektledare utan djup IT-kunskap.' },
  { title: 'Facility management – lokalbokning & arbetsorder', situation: 'early', customer_type: 'Fastighetsförvaltare', brief: 'Demonstrera ett system för facility management med lokalbokning, löpande underhållsschema och arbetsorder. Visa hur felanmälningar hanteras från inkommande till åtgärdad. Inkludera enkel statistik över nyttjandegrader.' },
  { title: 'Upphandlingsportal för bygg', situation: 'early', customer_type: 'Byggherre', brief: 'Visa en digital upphandlingsportal för byggprojekt. Inkludera anbud per entreprenadd, leverantörsutvärdering, status per upphandling och avtalsoversikt. Professionellt och affärsmässigt utseende.' },
  { title: 'StreamBIM – dokumenthantering & kvalitet', situation: 'proposal', customer_type: 'Totalentreprenör', brief: 'En konkret mockup av hur StreamBIM kan användas för deras projekt. Visa modellvisare med kopplade ritningar, checklistor för kvalitetskontroll per rum/zon, och avvikelserapportering med foto-bilaga. Inkludera statusöversikt per disciplin.' },
  { title: 'Dalux Field – besiktningsapp', situation: 'proposal', customer_type: 'Fastighetsägare', brief: 'Mockup av Dalux Field för deras slutbesiktningsprocess. Visa checklistor per besiktningspunkt, kartvyer med hotspots, fotodokumentation, och sammanfattningsrapport med öppna anmärkningar och ansvariga. Inkludera besiktningsprotokoll-vy.' },
  { title: 'Ekonomiuppföljning – byggprojekt', situation: 'proposal', customer_type: 'Byggherre', brief: 'En dashboard för ekonomisk uppföljning av ett nybyggnadsprojekt på 85 Mkr. Visa utfall vs budget per entreprenadd, prognos och avvikelseanalys, ÄTA-hantering och kassaflödesdiagram. Svenska belopp och realistiska siffror.' },
  { title: 'Digital relationshandling – förvaltningsdokumentation', situation: 'proposal', customer_type: 'Fastighetsägare', brief: 'Visa hur digital relationshandling och förvaltningsdokumentation kan levereras efter ett projekt. Inkludera ritningsregister, driftinstruktioner, garantihandlingar och garantiuppföljning. Intuitivt och strukturerat för förvaltningspersonal.' },
  { title: 'BIM 360 – projektledardashboard', situation: 'proposal', customer_type: 'Projektledare', brief: 'Mockup av BIM 360 för projektledning av ett kommersiellt byggprojekt. Visa RFI-hantering, submittal-status, schedule-overview och team-aktivitet. Inkludera notifikationscenter och snabbåtgärder. Tekniskt och informationstätt.' },
  { title: 'Energikartläggning – fastighetsbestånd', situation: 'proposal', customer_type: 'Fastighetsbolag', brief: 'En rapport-mockup för energikartläggning av ett fastighetsbestånd med 12 byggnader. Visa energiprestanda per fastighet, jämförelse mot BBR-krav, potentiella åtgärder med ROI-kalkyl och CO₂-besparing. Premiumkänsla.' },
  { title: 'Veckorapport – byggproduktion', situation: 'active', customer_type: 'Byggherre', brief: 'Statusrapport för pågående byggprojekt vecka 18. Visa: genomförda aktiviteter, avvikelser mot tidplan, fotodokumentation från arbetsplatsen, nästa veckas planering och ekonomisk ögonblicksbild. Rapporten ska kännas som ett levande dokument.' },
  { title: 'Avvikelserapport – kritiska ärenden', situation: 'active', customer_type: 'Projektledare', brief: 'En avvikelserapport för ett pågående projekt med fokus på kritiska ärenden. Visa öppna avvikelser per allvarlighetsgrad, ansvarig och deadline, åtgärdsstatus med historik och trendanalys. Inkludera filtreringsmöjligheter per disciplin.' },
  { title: 'Ekonomisk prognos – Q2-rapport', situation: 'active', customer_type: 'CFO/Ekonomiavdelning', brief: 'En ekonomisk kvartalsrapport för byggprojektportfölj. Visa budgetutfall per projekt, prognos vs plan, reservanvändning, ÄTA-status och kassaflödeskurva. Professionellt och exakt, anpassat för ekonomiavdelningen.' },
  { title: 'Byggmötes-app – protokoll & uppföljning', situation: 'active', customer_type: 'Byggentreprenör', brief: 'Digital byggmötesapp för löpande byggmöten. Visa mötesprotokoll med åtgärdspunkter, ansvarsfördelning, deadline och status (öppen/åtgärdad/försenad). Inkludera möteshistorik och mötesdeltagarlista. Praktisk och enkel design.' },
  { title: 'Dokumentkontroll – ritningsregister', situation: 'active', customer_type: 'BIM-samordnare', brief: 'Ritningsregister och dokumentkontroll för ett pågående projekt. Visa revisionsstatus per ritning (gällande/ersatt/för granskning), distributionstabeller, granskningskommentarer och godkännandeflöde. Tekniskt och detaljerat.' },
  { title: 'Kvalitetsledning – IKU & certifiering', situation: 'active', customer_type: 'Kvalitetschef', brief: 'Kvalitetslednings-dashboard för en löpande certifieringsprocess. Visa status per IKU-punkt, granskningsschema, öppna kvalitetsavvikelser, interna kontrollronder och certifieringsstatus. Strukturerat och metodiskt utseende.' },
];

// ── Overview section ──────────────────────────────────────────────────────────
function OverviewSection() {
  const [stats, setStats] = useState({ total: 0, thisMonth: 0, drafts: 0, cost: 0, topIterated: [] });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      getDocs(query(collection(db, 'applab_apps'), where('type', '==', 'mockup'), orderBy('versions_count', 'desc'), limit(5))),
      getDocs(query(collection(db, 'applab_apps'), where('type', '==', 'mockup'))),
      getDocs(query(collection(db, 'mockup_drafts'))),
      getDocs(query(collection(db, 'applab_usage'), where('block', '>=', 'mockup'), where('block', '<', 'mockupz'))),
    ]).then(([topSnap, allSnap, draftsSnap, usageSnap]) => {
      const now = new Date();
      const thisMonthStart = new Date(now.getFullYear(), now.getMonth(), 1);
      const monthDocs = allSnap.docs.filter(d => {
        const ts = d.data().createdAt;
        if (!ts) return false;
        const date = ts.toDate ? ts.toDate() : new Date(ts);
        return date >= thisMonthStart;
      });
      const totalCost = usageSnap.docs.reduce((s, d) => s + (d.data().cost_usd || 0), 0);
      setStats({
        total: allSnap.size,
        thisMonth: monthDocs.length,
        drafts: draftsSnap.size,
        cost: totalCost,
        topIterated: topSnap.docs.map(d => ({ id: d.id, name: d.data().name || '—', versions: d.data().versions_count || 0 })),
      });
    }).catch(() => {}).finally(() => setLoading(false));
  }, []);

  if (loading) return <CircularProgress size={24} sx={{ color: APPLAB_ACCENT }} />;

  return (
    <Box>
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', md: '1fr 1fr 1fr 1fr' }, gap: 2, mb: 4 }}>
        {[
          { label: 'Mockups totalt', value: stats.total },
          { label: 'Skapade denna månad', value: stats.thisMonth },
          { label: 'Aktiva utkast', value: stats.drafts },
          { label: 'Total kostnad', value: `$${stats.cost.toFixed(2)}` },
        ].map(({ label, value }) => (
          <Paper key={label} sx={{ p: 2.5, bgcolor: '#1a1a1a', borderRadius: 2 }}>
            <Typography fontSize="0.7rem" sx={{ color: '#666', mb: 0.5, textTransform: 'uppercase', letterSpacing: 1 }}>{label}</Typography>
            <Typography fontSize="1.6rem" fontWeight={800} sx={{ color: 'white', lineHeight: 1 }}>{value}</Typography>
          </Paper>
        ))}
      </Box>

      <Paper sx={{ p: 3, bgcolor: '#1a1a1a', borderRadius: 2 }}>
        <Typography fontWeight={700} fontSize="0.85rem" sx={{ color: 'white', mb: 2 }}>Top 5 mest itererade</Typography>
        {stats.topIterated.length === 0 ? (
          <Typography fontSize="0.78rem" sx={{ color: '#555' }}>Inga mockups ännu.</Typography>
        ) : (
          <Table size="small">
            <TableHead>
              <TableRow>
                {['Namn', 'Versioner'].map(h => <TableCell key={h} sx={headSx}>{h}</TableCell>)}
              </TableRow>
            </TableHead>
            <TableBody>
              {stats.topIterated.map(m => (
                <TableRow key={m.id}>
                  <TableCell sx={cellSx}>{m.name}</TableCell>
                  <TableCell sx={cellSx}>{m.versions}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Paper>
    </Box>
  );
}

// ── User quotas section ───────────────────────────────────────────────────────
function UserQuotasSection() {
  const [quotas, setQuotas] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    getDocs(collection(db, 'applab_quotas')).then(snap => {
      setQuotas(snap.docs.map(d => ({ id: d.id, ...d.data() })).filter(q =>
        (q.brief_improve_count || 0) > 0 || (q.brief_suggest_count || 0) > 0 ||
        (q.mockup_generate_count || 0) > 0
      ));
    }).catch(() => {}).finally(() => setLoading(false));
  }, []);

  if (loading) return <CircularProgress size={24} sx={{ color: APPLAB_ACCENT }} />;
  if (quotas.length === 0) return <Typography fontSize="0.78rem" sx={{ color: '#555' }}>Inga mockup-specifika kvoter registrerade ännu.</Typography>;

  return (
    <Box sx={{ overflowX: 'auto' }}>
      <Table size="small">
        <TableHead>
          <TableRow>
            {['UID', 'Brief Förbättra', 'Brief Förslag', 'Genereringar'].map(h => <TableCell key={h} sx={headSx}>{h}</TableCell>)}
          </TableRow>
        </TableHead>
        <TableBody>
          {quotas.map(q => (
            <TableRow key={q.id} sx={{ '&:hover': { bgcolor: 'rgba(255,255,255,0.03)' } }}>
              <TableCell sx={{ ...cellSx, fontFamily: 'monospace' }}>{q.id.slice(0, 10)}…</TableCell>
              <TableCell sx={cellSx}>{q.brief_improve_count || 0}</TableCell>
              <TableCell sx={cellSx}>{q.brief_suggest_count || 0}</TableCell>
              <TableCell sx={cellSx}>{q.mockup_generate_count || 0}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Box>
  );
}

// ── Reference library section ─────────────────────────────────────────────────
function ReferenceLibrarySection() {
  const [refs, setRefs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState({ title: '', description: '', url: '', situation: 'early', tags: '' });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const unsub = onSnapshot(
      query(collection(db, 'applab_mockup_references'), orderBy('createdAt', 'desc')),
      snap => { setRefs(snap.docs.map(d => ({ id: d.id, ...d.data() }))); setLoading(false); },
      () => setLoading(false)
    );
    return unsub;
  }, []);

  async function toggleActive(id, current) {
    await updateDoc(doc(db, 'applab_mockup_references', id), { active: !current });
  }

  async function addRef() {
    if (!form.title.trim() || !form.url.trim()) return;
    setSaving(true);
    await addDoc(collection(db, 'applab_mockup_references'), {
      ...form,
      tags: form.tags.split(',').map(t => t.trim()).filter(Boolean),
      active: true,
      createdAt: serverTimestamp(),
    });
    setForm({ title: '', description: '', url: '', situation: 'early', tags: '' });
    setAdding(false);
    setSaving(false);
  }

  async function removeRef(id) {
    if (!window.confirm('Ta bort referens?')) return;
    await deleteDoc(doc(db, 'applab_mockup_references', id));
  }

  if (loading) return <CircularProgress size={24} sx={{ color: APPLAB_ACCENT }} />;

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography fontSize="0.82rem" sx={{ color: '#888' }}>Referens-mockups som konsulter kan inspireras av.</Typography>
        <Button
          size="small" startIcon={<AddIcon sx={{ fontSize: 14 }} />}
          onClick={() => setAdding(a => !a)}
          sx={{ color: APPLAB_ACCENT, textTransform: 'none', fontSize: '0.8rem' }}
        >
          Lägg till
        </Button>
      </Box>

      {adding && (
        <Paper sx={{ p: 2, bgcolor: '#1e1e1e', borderRadius: 2, mb: 2 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            <TextField label="Titel" size="small" value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))} sx={tfSx} />
            <TextField label="Beskrivning" size="small" value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} sx={tfSx} />
            <TextField label="URL (HTML-fil)" size="small" value={form.url} onChange={e => setForm(f => ({ ...f, url: e.target.value }))} placeholder="https://..." sx={tfSx} />
            <Select size="small" value={form.situation} onChange={e => setForm(f => ({ ...f, situation: e.target.value }))}
              sx={{ color: 'white', bgcolor: 'rgba(255,255,255,0.04)', '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,255,255,0.12)' } }}>
              <MenuItem value="early">Tidig dialog</MenuItem>
              <MenuItem value="proposal">Konkret offert</MenuItem>
              <MenuItem value="active">Pågående projekt</MenuItem>
            </Select>
            <TextField label="Taggar (kommaseparerade)" size="small" value={form.tags} onChange={e => setForm(f => ({ ...f, tags: e.target.value }))} sx={tfSx} />
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button size="small" onClick={() => setAdding(false)} sx={{ color: '#888', textTransform: 'none' }}>Avbryt</Button>
              <Button size="small" variant="contained" onClick={addRef} disabled={saving}
                sx={{ bgcolor: APPLAB_ACCENT, textTransform: 'none' }}>
                {saving ? 'Sparar…' : 'Spara'}
              </Button>
            </Box>
          </Box>
        </Paper>
      )}

      {refs.length === 0 ? (
        <Typography fontSize="0.78rem" sx={{ color: '#555' }}>Inga referenser ännu.</Typography>
      ) : (
        <Table size="small">
          <TableHead>
            <TableRow>
              {['Titel', 'Situation', 'Taggar', 'Aktiv', ''].map(h => <TableCell key={h} sx={headSx}>{h}</TableCell>)}
            </TableRow>
          </TableHead>
          <TableBody>
            {refs.map(r => (
              <TableRow key={r.id} sx={{ '&:hover': { bgcolor: 'rgba(255,255,255,0.03)' } }}>
                <TableCell sx={cellSx}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    {r.title}
                    <IconButton size="small" href={r.url} target="_blank" rel="noopener" sx={{ color: '#555', p: 0.25 }}>
                      <OpenInNewIcon sx={{ fontSize: 12 }} />
                    </IconButton>
                  </Box>
                </TableCell>
                <TableCell sx={cellSx}>{r.situation}</TableCell>
                <TableCell sx={cellSx}>{(r.tags || []).join(', ') || '—'}</TableCell>
                <TableCell sx={cellSx}>
                  <Switch size="small" checked={!!r.active} onChange={() => toggleActive(r.id, r.active)}
                    sx={{ '& .MuiSwitch-thumb': { bgcolor: r.active ? APPLAB_ACCENT : '#555' } }} />
                </TableCell>
                <TableCell sx={cellSx}>
                  <DeleteIcon sx={{ fontSize: 16, color: '#444', cursor: 'pointer', '&:hover': { color: '#f44336' } }}
                    onClick={() => removeRef(r.id)} />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </Box>
  );
}

// ── Brief examples section ────────────────────────────────────────────────────
function BriefExamplesSection() {
  const [examples, setExamples] = useState([]);
  const [loading, setLoading] = useState(true);
  const [seeding, setSeeding] = useState(false);
  const [seedDone, setSeedDone] = useState(false);
  const [preview, setPreview] = useState(null);

  useEffect(() => {
    const unsub = onSnapshot(
      query(collection(db, 'applab_mockup_brief_examples'), orderBy('createdAt', 'desc')),
      snap => { setExamples(snap.docs.map(d => ({ id: d.id, ...d.data() }))); setLoading(false); },
      () => setLoading(false)
    );
    return unsub;
  }, []);

  async function seed() {
    if (!window.confirm(`Seeda ${SEED_EXAMPLES.length} brief-exempel? Befintliga poster påverkas ej.`)) return;
    setSeeding(true);
    for (const ex of SEED_EXAMPLES) {
      await addDoc(collection(db, 'applab_mockup_brief_examples'), {
        ...ex,
        active: true,
        createdAt: serverTimestamp(),
      });
    }
    setSeeding(false);
    setSeedDone(true);
    setTimeout(() => setSeedDone(false), 3000);
  }

  async function removeExample(id) {
    if (!window.confirm('Ta bort exempel?')) return;
    await deleteDoc(doc(db, 'applab_mockup_brief_examples', id));
  }

  const situationLabel = { early: 'Tidig dialog', proposal: 'Konkret offert', active: 'Pågående projekt' };
  const situationColor = { early: '#4CAF50', proposal: APPLAB_ACCENT, active: '#FF9800' };

  if (loading) return <CircularProgress size={24} sx={{ color: APPLAB_ACCENT }} />;

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography fontSize="0.82rem" sx={{ color: '#888' }}>{examples.length} exempel inlagda</Typography>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
          {seedDone && <Typography fontSize="0.78rem" sx={{ color: '#4CAF50' }}>Seedat!</Typography>}
          <Button
            size="small" variant="outlined"
            onClick={seed} disabled={seeding}
            startIcon={seeding ? <CircularProgress size={12} /> : null}
            sx={{ color: APPLAB_ACCENT, borderColor: APPLAB_ACCENT + '55', textTransform: 'none', fontSize: '0.78rem' }}
          >
            {seeding ? 'Seedar…' : `Seeda ${SEED_EXAMPLES.length} exempel`}
          </Button>
        </Box>
      </Box>

      {examples.length === 0 ? (
        <Typography fontSize="0.78rem" sx={{ color: '#555' }}>Inga exempel ännu. Klicka "Seeda" för att fylla på.</Typography>
      ) : (
        <Table size="small">
          <TableHead>
            <TableRow>
              {['Titel', 'Situation', 'Kund typ', ''].map(h => <TableCell key={h} sx={headSx}>{h}</TableCell>)}
            </TableRow>
          </TableHead>
          <TableBody>
            {examples.map(ex => (
              <TableRow key={ex.id} sx={{ '&:hover': { bgcolor: 'rgba(255,255,255,0.03)', cursor: 'pointer' } }}
                onClick={() => setPreview(ex)}>
                <TableCell sx={cellSx}>{ex.title}</TableCell>
                <TableCell sx={cellSx}>
                  <Chip label={situationLabel[ex.situation] || ex.situation} size="small"
                    sx={{ height: 18, fontSize: '0.6rem', bgcolor: (situationColor[ex.situation] || '#888') + '22', color: situationColor[ex.situation] || '#888' }} />
                </TableCell>
                <TableCell sx={cellSx}>{ex.customer_type || '—'}</TableCell>
                <TableCell sx={cellSx} onClick={e => e.stopPropagation()}>
                  <DeleteIcon sx={{ fontSize: 16, color: '#444', cursor: 'pointer', '&:hover': { color: '#f44336' } }}
                    onClick={() => removeExample(ex.id)} />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}

      <Dialog open={!!preview} onClose={() => setPreview(null)}
        PaperProps={{ sx: { bgcolor: '#1e1e1e', color: 'white', minWidth: 480, maxWidth: 600 } }}>
        {preview && (
          <>
            <DialogTitle sx={{ color: 'white', fontSize: '1rem' }}>{preview.title}</DialogTitle>
            <DialogContent>
              <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                <Chip label={situationLabel[preview.situation] || preview.situation} size="small"
                  sx={{ bgcolor: (situationColor[preview.situation] || '#888') + '22', color: situationColor[preview.situation] || '#888' }} />
                {preview.customer_type && (
                  <Chip label={preview.customer_type} size="small" sx={{ bgcolor: 'rgba(255,255,255,0.06)', color: '#888' }} />
                )}
              </Box>
              <Typography fontSize="0.82rem" sx={{ color: '#ccc', lineHeight: 1.7, whiteSpace: 'pre-wrap' }}>
                {preview.brief}
              </Typography>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setPreview(null)} sx={{ color: '#888', textTransform: 'none' }}>Stäng</Button>
            </DialogActions>
          </>
        )}
      </Dialog>
    </Box>
  );
}

// ── Settings section ──────────────────────────────────────────────────────────
function SettingsSection() {
  const [config, setConfig] = useState(null);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    getDoc(doc(db, 'applab_config', 'global')).then(snap => {
      if (snap.exists()) setConfig(snap.data());
    }).catch(() => {});
  }, []);

  async function save() {
    if (!config) return;
    setSaving(true);
    await updateDoc(doc(db, 'applab_config', 'global'), {
      mockup_default_model:   config.mockup_default_model   || 'claude-sonnet-4-6',
      mockup_kill_switch:     !!config.mockup_kill_switch,
      mockup_per_request_limit: Number(config.mockup_per_request_limit || 8192),
      updated_at: serverTimestamp(),
    });
    setSaving(false);
    setSaved(true);
    setTimeout(() => setSaved(false), 2000);
  }

  if (!config) return <CircularProgress size={24} sx={{ color: APPLAB_ACCENT }} />;

  return (
    <Box sx={{ maxWidth: 480 }}>
      <Paper sx={{ p: 3, bgcolor: '#1a1a1a', borderRadius: 2, mb: 2 }}>
        <Typography fontWeight={700} fontSize="0.85rem" sx={{ color: 'white', mb: 2 }}>MockupN-inställningar</Typography>

        <Box sx={{ mb: 2 }}>
          <Typography fontSize="0.8rem" sx={{ color: '#888', mb: 0.75 }}>Standardmodell</Typography>
          <Select size="small" fullWidth
            value={config.mockup_default_model || 'claude-sonnet-4-6'}
            onChange={e => setConfig(c => ({ ...c, mockup_default_model: e.target.value }))}
            sx={{ color: 'white', bgcolor: 'rgba(255,255,255,0.04)', '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,255,255,0.12)' } }}>
            <MenuItem value="claude-sonnet-4-6">Sonnet 4.6 (standard)</MenuItem>
            <MenuItem value="claude-opus-4-7">Opus 4.7 (premium)</MenuItem>
          </Select>
        </Box>

        <Box sx={{ mb: 2 }}>
          <TextField
            label="Max output tokens per generering"
            type="number" size="small" fullWidth
            value={config.mockup_per_request_limit || 8192}
            onChange={e => setConfig(c => ({ ...c, mockup_per_request_limit: e.target.value }))}
            sx={tfSx}
          />
        </Box>

        <FormControlLabel
          control={
            <Switch
              checked={!!config.mockup_kill_switch}
              onChange={e => setConfig(c => ({ ...c, mockup_kill_switch: e.target.checked }))}
              size="small"
              sx={{ '& .MuiSwitch-thumb': { bgcolor: config.mockup_kill_switch ? '#f44336' : '#555' } }}
            />
          }
          label={<Typography fontSize="0.85rem" sx={{ color: config.mockup_kill_switch ? '#f44336' : '#ccc' }}>
            Kill-switch (inaktiverar all mockup-generering)
          </Typography>}
        />
      </Paper>

      {saved && <Alert severity="success" sx={{ mb: 2, bgcolor: '#1a2e1a', color: '#81c784' }}>Sparat!</Alert>}
      <Button
        variant="contained" onClick={save} disabled={saving}
        sx={{ bgcolor: APPLAB_ACCENT, '&:hover': { bgcolor: '#00796B' }, textTransform: 'none' }}
      >
        {saving ? 'Sparar...' : 'Spara inställningar'}
      </Button>
    </Box>
  );
}

// ── Cost chart section ────────────────────────────────────────────────────────
function CostSection() {
  const [days, setDays] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    getDocs(query(
      collection(db, 'applab_usage'),
      where('block', '>=', 'mockup'),
      where('block', '<', 'mockupz'),
      orderBy('block'),
      orderBy('timestamp', 'desc'),
      limit(500)
    )).then(snap => {
      const buckets = {};
      snap.docs.forEach(d => {
        const data = d.data();
        const ts = data.timestamp;
        if (!ts) return;
        const date = (ts.toDate ? ts.toDate() : new Date(ts)).toISOString().slice(0, 10);
        buckets[date] = (buckets[date] || 0) + (data.cost_usd || 0);
      });
      const sorted = Object.entries(buckets).sort().slice(-30).map(([date, cost]) => ({ date, cost }));
      setDays(sorted);
    }).catch(() => {}).finally(() => setLoading(false));
  }, []);

  if (loading) return <CircularProgress size={24} sx={{ color: APPLAB_ACCENT }} />;
  if (days.length === 0) return <Typography fontSize="0.78rem" sx={{ color: '#555' }}>Ingen mockup-kostnad registrerad ännu.</Typography>;

  const maxCost = Math.max(...days.map(d => d.cost), 0.0001);
  const totalCost = days.reduce((s, d) => s + d.cost, 0);

  return (
    <Box>
      <Typography fontSize="0.82rem" sx={{ color: '#888', mb: 2 }}>
        Mockup-kostnad senaste 30 dagarna · Total: ${totalCost.toFixed(4)}
      </Typography>
      <Paper sx={{ p: 3, bgcolor: '#1a1a1a', borderRadius: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: '3px', height: 120 }}>
          {days.map(d => (
            <Box key={d.date} sx={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5 }}>
              <Box
                title={`${d.date}: $${d.cost.toFixed(5)}`}
                sx={{
                  width: '100%', borderRadius: '2px 2px 0 0',
                  bgcolor: APPLAB_ACCENT + 'aa',
                  height: `${Math.max(2, (d.cost / maxCost) * 100)}px`,
                  cursor: 'default',
                  transition: 'background 0.12s',
                  '&:hover': { bgcolor: APPLAB_ACCENT },
                }}
              />
            </Box>
          ))}
        </Box>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 0.75 }}>
          <Typography fontSize="0.68rem" sx={{ color: '#444' }}>{days[0]?.date}</Typography>
          <Typography fontSize="0.68rem" sx={{ color: '#444' }}>{days[days.length - 1]?.date}</Typography>
        </Box>
      </Paper>
    </Box>
  );
}

// ── Shared TextField style ────────────────────────────────────────────────────
const tfSx = {
  '& .MuiOutlinedInput-root': { color: 'white', bgcolor: 'rgba(255,255,255,0.04)',
    '& fieldset': { borderColor: 'rgba(255,255,255,0.12)' },
    '&:hover fieldset': { borderColor: 'rgba(255,255,255,0.25)' },
    '&.Mui-focused fieldset': { borderColor: APPLAB_ACCENT } },
  '& label': { color: '#888' },
  '& .MuiFormHelperText-root': { color: '#555' },
};

// ── Main export ───────────────────────────────────────────────────────────────
export default function MockupAdminTab() {
  const [section, setSection] = useState(0);

  return (
    <Box>
      <Box sx={{ display: 'flex', gap: 1, mb: 3, flexWrap: 'wrap' }}>
        {SECTIONS.map((s, i) => (
          <Chip
            key={s} label={s} onClick={() => setSection(i)}
            sx={{
              bgcolor: section === i ? APPLAB_ACCENT + '22' : 'rgba(255,255,255,0.06)',
              color: section === i ? APPLAB_ACCENT : '#888',
              border: `1px solid ${section === i ? APPLAB_ACCENT + '55' : 'transparent'}`,
              cursor: 'pointer', fontWeight: section === i ? 700 : 400,
            }}
          />
        ))}
      </Box>

      {section === 0 && <OverviewSection />}
      {section === 1 && <UserQuotasSection />}
      {section === 2 && <ReferenceLibrarySection />}
      {section === 3 && <BriefExamplesSection />}
      {section === 4 && <SettingsSection />}
      {section === 5 && <CostSection />}
    </Box>
  );
}
